// src/hooks/useAuth.tsx
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { signUp as apiSignUp, login as apiLogin, logout as apiLogout, refreshSession as apiRefreshSession, changePassword as apiChangePassword, verifyOtp as apiVerifyOtp } from '@/lib/auth';
import { resendOtp as apiResendOtp } from '@/services/api/auth';
import { setAuthToken } from '@/services/api/client';
import { getAuthStore, setAuthStore, clearAuthStore } from '@/services/authStore';
import { getProfile } from '@/lib/users';
import type { UserProfile } from '@/lib/users';
import api from '@/lib/api';
import { getApiMessage, getApiSuccess } from '@/lib/apiMessage';

interface AuthContextType {
  user: UserProfile | null;
  sessionExists: boolean;
  loading: boolean;
  signUp: (email: string, password: string, companyName?: string) => Promise<{ error?: any }>;
  signIn: (email: string, password: string) => Promise<{ error?: any; requiresOtp?: boolean; data?: any }>;
  completeOtp: (loginOtp: string) => Promise<{ error?: any; data?: any }>;
  resendOtp: (username?: string) => Promise<{ error?: any; data?: any }>;
  signOut: (opts?: { silent?: boolean; message?: string }) => Promise<void>;
  refreshSession: () => Promise<{ success: boolean; error?: any }>;
  verifySession: () => Promise<{ isValid: boolean; needsRefresh: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Use this flag for compatibility: sessionExists true when we have a logged-in user
  // Consider a session to exist only when we have user metadata AND an access token saved.
  // This avoids treating the pre-OTP 'userData' (stored after username/password step) as a full session.
  const sessionExists = !!user && (typeof window !== 'undefined' ? !!getAuthStore()?.authToken : false);

  // Debug logging helper (keeps your verbose logs)
  function logState(msg: string, extra?: Record<string, any>) {
    console.log(`🔐 AUTH: ${msg}`, extra ?? {});
  }

  // Helper: pick the first existing field from a list of possible keys (case variants)
  function pickField<T = any>(obj: Record<string, any> | undefined, keys: string[]): T | null {
    if (!obj) return null;
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) return obj[k] as T;
    }
    return null;
  }

  // Load profile from backend (GET /auth/me or /users/me)
  const loadProfile = async () => {
    setLoading(true);
    try {
      // If a previous flow set a skip flag (for example immediately after OTP verification),
      // honor it once and remove it so we don't attempt to fetch /auth/me right away.
      try {
        const skip = sessionStorage.getItem('skipProfileLoad');
        if (skip === 'true') {
          sessionStorage.removeItem('skipProfileLoad');
          logState('Skipping profile load due to skipProfileLoad flag');
          setUser(null);
          return null;
        }
      } catch (e) {
        // ignore storage errors and proceed
      }
      // Check for super admin access parameters
      const urlParams = new URLSearchParams(window.location.search);
      const isSuperAdminAccess = urlParams.get('super_admin_access') === 'true';
      const partnerId = urlParams.get('partner_id');
      
      // Attempt to load profile. Do not rely solely on localStorage token because
      // the backend may use cookie-based sessions (HttpOnly cookies). We try
      // fetching the profile regardless so cookie sessions are supported.
      logState('Fetching current profile from /auth/me');
      const profile = await getProfile(); // wrapper calls /users/me or /auth/me
      
      // If this is super admin access on a partner subdomain, ensure we maintain super admin privileges
      if (isSuperAdminAccess && partnerId && profile) {
        logState('Super admin access detected on partner subdomain', { partnerId, userEmail: profile.email });
        // Store super admin access context
        try { sessionStorage.setItem('super_admin_access', 'true'); } catch (e) {}
        try { sessionStorage.setItem('super_admin_partner_id', partnerId); } catch (e) {}
      }
      
      setUser(profile ?? null);
      logState('Profile loaded', { id: profile?.id, email: profile?.email });
      return profile;
    } catch (err) {
      logState('No active session or profile fetch failed', { err });
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Run once on mount to establish session state
  useEffect(() => {
    // Do not auto-load profile from the server on mount. Instead, hydrate minimal
    // user metadata from localStorage (set during signIn) so header/profile can show
    // the user's name immediately without network calls.
    try {
  // Hydrate from sessionStorage (we persist minimal userData there for reload resilience)
  const raw = sessionStorage.getItem('userData');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          // Remove duplicate 'name' key in persisted data to avoid ambiguity with nameOfUser
          const sanitized = { ...(parsed || {}) } as Record<string, any>;
          if (sanitized.hasOwnProperty('name')) delete sanitized.name;

          // normalize the stored minimal fields into the UserProfile shape
          setUser({
            id: sanitized.id ?? sanitized.authUserId ?? null,
            email: sanitized.username ?? sanitized.email ?? null,
            name: sanitized.nameOfUser ?? null,
            role: sanitized.role ?? null,
            ...sanitized,
          } as UserProfile);
          // also populate in-memory auth store so interceptors can pick it up
          try { setAuthStore({ userData: sanitized }); } catch (e) {}
          // persist sanitized userData back to sessionStorage to remove legacy 'name' key
          try { sessionStorage.setItem('userData', JSON.stringify(sanitized)); } catch (e) {}
        } catch (e) {
          // ignore parse errors
        }
      }
    } catch (e) {
      // ignore storage errors
    }

    setLoading(false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for refresh failures (dispatched by api client) so we can sign the user out and redirect
  useEffect(() => {
    function onRefreshFailed(e: Event) {
      // Only sign out if we actually had a session (token present). During login
      // failures there is no token and we should not call logout.
      try {
        const hasToken = !!getAuthStore()?.authToken;
        if (!hasToken) return;
      } catch (_) {}

      console.warn('AuthProvider detected refresh failure, signing out...');
      (async () => {
        try {
          await signOut({ silent: true });
        } catch (_) {}
        try { window.location.href = '/auth'; } catch (_) {}
      })();
    }

    window.addEventListener('auth:refresh_failed', onRefreshFailed as EventListener);
    return () => window.removeEventListener('auth:refresh_failed', onRefreshFailed as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Actions ----------

  const signUp = async (email: string, password: string, companyName?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/dashboard`;
      const data = await apiSignUp(email, password, companyName, redirectUrl);
      toast({
        title: getApiMessage(data) || 'Account created',
        description: 'Please check your email to confirm registration (if applicable).',
      });
      // Optionally auto-load profile if backend signs in the user immediately
      await loadProfile();
      return { error: null };
    } catch (error: any) {
      console.error('SignUp error', error);
      toast({
        title: 'Sign up failed',
        description: error?.response?.data?.message ?? error?.message ?? 'Unable to sign up',
        variant: 'destructive',
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // note: backend expects "username" as key; our api login uses that name
      const data = await apiLogin(email, password);

      // If backend explicitly indicates failure, surface message and stop
      const successFlag = getApiSuccess(data);
      const apiMsg = getApiMessage(data);
      if (successFlag === false) {
        const msg = apiMsg || 'Invalid credentials';
        toast({ title: 'Sign in failed', description: msg, variant: 'destructive' });
        return { error: msg };
      }

      // If backend indicates OTP required (message contains 'OTP' or success false but OTP flow):
      const needsOtp = (successFlag === true || data?.success === true) && /otp/i.test(String(apiMsg ?? ''));
      if (needsOtp) {
  try { sessionStorage.setItem('pendingUsername', email); } catch (e) {}
  try { setAuthStore({ pendingUsername: email }); } catch (e) {}
        // Persist minimal metadata returned by the login step so header/profile can show the name
          try {
            const payload = (data && (data.data || data)) || {};
            // Persist only the requested minimal fields for the pre-OTP state
            const pendingUser = {
              username: payload.username ?? payload.email ?? email,
              authUserId: pickField<string>(payload, ['authUserId', 'AuthUserId', 'userId', 'UniqueId', 'id']),
              nameOfUser: pickField<string>(payload, ['nameOfUser', 'NameOfUser', 'name']) ?? payload.user?.name ?? null,
            };
            // Persist to sessionStorage for reload resilience and to in-memory store
            try { sessionStorage.setItem('userData', JSON.stringify(pendingUser)); } catch (e) {}
            try { setAuthStore({ pendingUsername: email, userData: pendingUser }); } catch (e) {}
          } catch (e) {
            // ignore storage errors
          }
        toast({ title: 'OTP Required', description: apiMsg || 'An OTP was sent to your email. Enter it to continue.' });
        return { error: null, requiresOtp: true, data };
      }

      // If backend returned token and signed in
      await loadProfile();
      toast({ title: 'Welcome back!', description: apiMsg || 'Signed in successfully.' });
      return { error: null, requiresOtp: false, data };
    } catch (err: any) {
      // On CORS/preflight/basic-auth scenarios, axios may throw without response.
      // Prefer backend message when present; otherwise show a generic one.
      const message = getApiMessage(err) || 'Unable to sign in';
      console.error('SignIn error', err);
      toast({ title: 'Sign in failed', description: message, variant: 'destructive' });
      return { error: message };
    }
  };

  const completeOtp = async (loginOtp: string) => {
    try {
  const username = getAuthStore()?.pendingUsername ?? sessionStorage.getItem('pendingUsername');
      if (!username) return { error: 'No pending login found. Please sign in again.' };
      const data = await apiVerifyOtp(username, loginOtp);
      // Do NOT automatically load profile here. Backend sets token/cookie in
      // verifyOtp (services/auth.verifyOtp) and UI should navigate to dashboard
      // immediately. If the user opens profile, the profile API will be called
      // on demand.
      // Ensure token is saved client-side if present in the verify response (some flows)
      try {
        const token = data?.token || data?.accessToken || data?.data?.token || data?.data?.accessToken;
        if (token) {
          // setAuthToken will update the in-memory auth token and axios headers
          setAuthToken(token);
          try { setAuthStore({ authToken: token }); } catch (e) {}
        }
      } catch (e) {
        // ignore
      }
  toast({ title: 'OTP Verified', description: getApiMessage(data) || 'You are signed in.' });
      // Persist minimal user info (if provided by backend) so header/profile can show it
      try {
        const payload = (data && (data.data || data)) || {};
        const userInfo = {
          id: pickField<string>(payload, ['authUserId', 'AuthUserId', 'userId', 'UniqueId', 'id']),
          email: payload.username ?? payload.email ?? payload.userEmail ?? null,
          // Persist nameOfUser only to avoid duplicate 'name' field
          nameOfUser: pickField<string>(payload, ['nameOfUser', 'NameOfUser']) ?? payload.user?.name ?? null,
          role: payload.role ?? payload.Role ?? payload.user?.role ?? null,
        };
        // Merge with existing sessionStorage/in-memory data to preserve authUserId
        try {
          const existing = (() => {
            try {
              const s = getAuthStore()?.userData ?? null;
              if (s) return s;
            } catch (e) {}
            try { return JSON.parse(sessionStorage.getItem('userData') || 'null'); } catch (e) { return null; }
          })() as Record<string, any> | null;

          const merged = {
            ...(existing || {}),
            ...(userInfo || {}),
          } as Record<string, any>;
          // remove legacy 'name' key if present
          if (merged.hasOwnProperty('name')) delete merged.name;
          // Prefer existing authUserId/id when the backend didn't return one
          merged.id = userInfo.id ?? existing?.authUserId ?? existing?.id ?? merged.id ?? null;
          merged.authUserId = merged.authUserId ?? merged.id ?? null;

          try { sessionStorage.setItem('userData', JSON.stringify(merged)); } catch (e) {}
          try { setAuthStore({ userData: merged }); } catch (e) {}
        } catch (e) {
          // ignore storage errors
        }
      } catch (e) {
        // ignore storage errors
      }

      try { sessionStorage.removeItem('pendingUsername'); } catch (e) {}
      try { setAuthStore({ pendingUsername: null }); } catch (e) {}

    // Keep the flow simple: do not force navigation here. The page that called
    // completeOtp (OTP page) should handle navigation to /dashboard. This avoids
    // unexpected redirects during normal flow and keeps control at the page level.

      return { error: null, data };
    } catch (err: any) {
      const message = getApiMessage(err) || 'OTP verification failed';
      console.error('completeOtp error', err);
      toast({ title: 'OTP Verification failed', description: message, variant: 'destructive' });
      return { error: message };
    }
  };

  const resendOtp = async (username?: string) => {
    try {
      const u = username ?? getAuthStore()?.pendingUsername ?? sessionStorage.getItem('pendingUsername');
      if (!u) return { error: 'No pending username' };
      const data = await apiResendOtp(u);
      return { error: null, data };
    } catch (err: any) {
      console.error('resendOtp error', err);
      return { error: getApiMessage(err) || err };
    }
  };

  const signOut = async (opts?: { silent?: boolean; message?: string }) => {
    try {
      console.log('🔐 AUTH: Starting signOut process...');
      // Determine authUserId for query param
      let authUserId: string | null = null;
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) {
          const parsed = JSON.parse(raw);
          authUserId = parsed?.authUserId || parsed?.id || null;
        }
      } catch (_) {}

      await apiLogout(authUserId);

      // Clear user state & storage
      setUser(null);
      try { sessionStorage.clear(); } catch (_) {}
      try { setAuthStore({ authToken: null, pendingUsername: null, userData: null, workspaceData: null }); } catch (e) {}
      try { delete (api.defaults.headers as any).Authorization; } catch (_) {}

      if (!opts?.silent) {
        toast({ title: opts?.message ?? 'Logged out successfully', description: '' });
      }
    } catch (error: any) {
      console.error('🔐 AUTH: SignOut error', error);
      // Fallback clear
      setUser(null);
      try { sessionStorage.clear(); } catch (_) {}
      try { setAuthStore({ authToken: null, pendingUsername: null, userData: null, workspaceData: null }); } catch (e) {}
      try { delete (api.defaults.headers as any).Authorization; } catch (_) {}
      if (!opts?.silent) {
        toast({ title: opts?.message ?? 'Logged out successfully', description: '' });
      }
    } finally {
      // Navigate to auth page via router (no full reload)
      try { navigate('/auth', { replace: true }); } catch (_) {}
    }
  };

  const refreshSession = async () => {
    logState('Manual session refresh requested');
    try {
      const data = await apiRefreshSession();
      if (data?.token) {
        // token stored by apiRefreshSession
        logState('Session refreshed, new token stored');
        // reload profile
        await loadProfile();
        return { success: true };
      } else {
        logState('Refresh returned no token');
        return { success: false, error: 'no_token' };
      }
    } catch (error: any) {
      console.error('Refresh session error', error);
      return { success: false, error };
    }
  };

  const verifySession = async () => {
    logState('Verifying session');
    try {
      // If there is no user currently, not valid
      if (!user) return { isValid: false, needsRefresh: false };

      // Option A: if using JWT tokens, check expiry locally (not implemented here)
      // Option B: Ask backend if session valid via /auth/me
      try {
        const fresh = await getProfile();
        if (!fresh) return { isValid: false, needsRefresh: true };
        // check if token is near expiry: best if backend provides expiresAt; otherwise assume OK
        return { isValid: true, needsRefresh: false };
      } catch (err) {
        // Try refresh once
        const ref = await refreshSession();
        return { isValid: !!ref.success, needsRefresh: !ref.success };
      }
    } catch (error) {
      console.error('verifySession exception', error);
      return { isValid: false, needsRefresh: true };
    }
  };

  // expose context value
  const value: AuthContextType = {
    user,
    sessionExists,
    loading,
    signUp,
    signIn,
    completeOtp,
    resendOtp,
    signOut,
    refreshSession,
    verifySession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};